import {
  AnimationTriggerMetadata, animate, animateChild, group, query, state, style, transition, trigger,
} from '@angular/animations';

/**
 * The one place expansion motion is defined. Both triggers come from the scene editor's overlay
 * cards (`scene-editor.component.ts`), so an expansion anywhere in Settings or Setup reads as the
 * same gesture. Adopt a trigger; never re-declare a duration or curve in a component.
 */
export const EXPAND_MS = 220;
export const COLLAPSE_MS = 200;
export const EXPAND_BASE = `${EXPAND_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`;
export const COLLAPSE_BASE = `${COLLAPSE_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`;

/**
 * Used by `contentFade` only. 8px, not the editor's 40px, because that content sits inside a box
 * whose height is animated by something else; a large offset there reads as cropping, not motion.
 *
 * `expandFade` deliberately has NO lift: it animates its own height, and a transform on the same box
 * does not affect layout, so an offset there draws the growing row over its neighbour instead of
 * moving it. That was reported from a device as a stutter — worst where the row above sits flush.
 */
export const EXPAND_LIFT = '-8px';

/**
 * True when the OS asks for reduced motion. Nothing else in the app honours this yet.
 *
 * LIMITATION: the triggers below are static metadata, so this is sampled ONCE when the bundle is
 * evaluated. Toggling Reduce Motion mid-session does not take effect until a reload. Making it live
 * needs `animate('{{ t }}', …)` with params fed from a matchMedia listener, or an `@.disabled` host
 * binding — deliberately not done here.
 */
export function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Reduced motion keeps the appearance/disappearance but removes the movement. `reduced` is a
 * parameter (not read inline) so it is testable without stubbing matchMedia before module load.
 */
export function resolveTiming(base: string, reduced: boolean = prefersReducedMotion()): string {
  return reduced ? '0ms' : base;
}

export const EXPAND_TIMING = resolveTiming(EXPAND_BASE);
export const COLLAPSE_TIMING = resolveTiming(COLLAPSE_BASE);

/**
 * For content revealed and hidden in place. The element stays MOUNTED and switches between the
 * `'shut'` and `'open'` states — it is deliberately NOT created and destroyed with `*ngIf`.
 *
 * Why: Angular resolves `height: '*'` with a single synchronous `getComputedStyle()` in the same
 * frame the element is inserted (see `computeStyle` in @angular/animations/browser). A reveal whose
 * height comes from a lazily-hydrated Ionic component (`ion-toggle`, `ion-segment`, anything inside
 * `toggle-panel`) has no height in that frame, so the snapshot is 0 or near it. Measured on a child
 * that gains its height three frames after insertion, an `:enter` height animation produced
 * `0,0,0,…,0,44` — it sat still for the whole animation and then popped, which is exactly what a
 * device showed. Kept mounted, the content is already laid out, and the same measurement produced a
 * smooth `0 → 44` ramp.
 *
 * Consequences to respect at the call sites: the content is always in the DOM, so bind
 * `[attr.inert]` alongside this trigger to keep collapsed content out of the tab order, and do not
 * put anything here whose mere construction has side effects.
 *
 * Margins are animated too, because Angular does not do it for you and a margin-carrying reveal
 * would otherwise apply its full margin on the first frame and jump. There is no transform: height
 * supplies the movement, and a transform on this same box would not affect layout, so it would draw
 * the row over its neighbour instead of moving it.
 */
export const expandFade: AnimationTriggerMetadata = trigger('expandFade', [
  // Padding is zeroed as well as height and margins: box-sizing is border-box app-wide, so padding
  // floors the border-box height and a padded element (the day-type ion-segment carries 4px) would
  // never fully collapse — it left an 8px ghost strip.
  state('shut', style({
    height: '0', marginTop: '0', marginBottom: '0', paddingTop: '0', paddingBottom: '0',
    opacity: 0, overflow: 'hidden',
  })),
  // 'open' deliberately pins NOTHING. Putting `height: '*'` in a state makes Angular snapshot a
  // computed height the moment that state first applies — and for an element that starts open, that
  // is before its content has laid out, which pinned an inline `height: 0` forever. Measuring inside
  // the transition instead happens when the content is real.
  //
  // `overflow: hidden` is restated in BOTH transitions rather than left to the state. Angular erases
  // the from-styles before building keyframes, and overflow does not interpolate — so declared only
  // once it flips at the midpoint, which on a flex item lets `min-height: auto` reassert and clamp
  // the animated height, popping the row halfway through.
  //
  // animateChild lets a nested reveal (a motion row containing its own timer) run its own animation:
  // Angular otherwise skips a child trigger whenever an ancestor animates in the same flush, which
  // is exactly what a handler that clears both flags at once produces.
  transition('shut => open', group([
    animate(EXPAND_TIMING, style({
      height: '*', marginTop: '*', marginBottom: '*', paddingTop: '*', paddingBottom: '*',
      opacity: 1, overflow: 'hidden',
    })),
    query('@expandFade', animateChild(), { optional: true }),
  ])),
  transition('open => shut', [
    style({ overflow: 'hidden' }),
    group([
      animate(COLLAPSE_TIMING),
      query('@expandFade', animateChild(), { optional: true }),
    ]),
  ]),
]);

/**
 * For content that persists while something else animates its height — ion-accordion measures the
 * content and transitions `max-height` (see @ionic/core accordion.js). Animating height here too
 * would fight it, so this trigger only fades and lifts. `void => *` has no transition, so the
 * initial state applies instantly instead of animating on first render.
 */
export const contentFade: AnimationTriggerMetadata = trigger('contentFade', [
  state('collapsed', style({ opacity: 0, transform: `translateY(${EXPAND_LIFT})` })),
  state('expanded', style({ opacity: 1, transform: 'none' })),
  transition('collapsed => expanded', animate(EXPAND_TIMING)),
  transition('expanded => collapsed', animate(COLLAPSE_TIMING)),
]);

/**
 * The confirm card's backdrop (theme/_confirm-card.scss): a plain fade.
 */
export const cardBackdrop: AnimationTriggerMetadata = trigger('cardBackdrop', [
  transition(':enter', [style({ opacity: 0 }), animate('180ms ease-out', style({ opacity: 1 }))]),
  transition(':leave', [animate('200ms ease-in', style({ opacity: 0 }))]),
]);

/**
 * The confirm card itself: fades in while dropping to centre from above, the
 * scene editor's overlay-card motion.
 */
export const cardPop: AnimationTriggerMetadata = trigger('cardPop', [
  transition(':enter', [
    style({ opacity: 0, transform: 'translateY(-40px)' }),
    animate(EXPAND_TIMING, style({ opacity: 1, transform: 'translateY(0)' })),
  ]),
  transition(':leave', [
    animate(COLLAPSE_TIMING, style({ opacity: 0, transform: 'translateY(-40px)' })),
  ]),
]);
